import React, { useEffect, useState } from "react";

import {
  Button,
  DocumentList,
  InfoPanel,
  Label,
  Modal,
  Typography,
  UploadBox,
  IconWrapper,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as DocumentApprovedIcon } from "@king-ict/eupisi-ui/components/Icon/DocumentApprovedIcon.svg";
import { ReactComponent as DocumentSendSuccessIcon } from "@king-ict/eupisi-ui/components/Icon/SuccessRequest.svg";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import { toFormData, isEmpty } from "@king-ict/eupisi-ui/utils";
import { hrDate, hrShortTime } from "@king-ict/eupisi-ui/utils";
import saveAs from "file-saver";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  getInstitutionNameByRequestId as getInstitutionNameApi,
  getPhysicalExaminationDocument,
  getPhysicalExamination,
  sendPhysicalExaminationDocumentation,
} from "../../../api/kindergartenApi";
import { useBusinessRule } from "../../../hooks/businessRule/useBusinessRule";
import InstitutionName from "../../../models/Institution/InstitutionName";
import PhysicalExaminationData from "../../../models/PhysicalExamination/PhysicalExaminationData";
import PhysicalExaminationStatusEnum from "../../../models/PhysicalExamination/PhysicalExaminationStatusEnum";
import SendPhysicalExaminationDocumentation from "../../../models/PhysicalExamination/SendPhysicalExaminationDocumentation";
import RequestBasicInfo from "../../../models/Request/RequestBasicInfo";
import { dashboard } from "../../../routes/routes";
import styles from "./PhysicalExamination.module.scss";

interface Props {
  requestBasicInfo: RequestBasicInfo;
}

export default function PhysicalExaminationPage({ requestBasicInfo }: Props) {
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [lastUploadDocumentId, setLastUploadDocumentId] = useState<number>(0);
  const [physicalExamination, setPhysicalExamination] = useState<
    PhysicalExaminationData | undefined
  >(undefined);
  const [
    showMaxDoucmentsError,
    setShowMaxDoucmentsError,
  ] = React.useState<boolean>(false);
  const { isPhysicalExaminationAllowed } = useBusinessRule();
  const history = useHistory();
  const [institutionName, setInstitutionName] = useState<InstitutionName>(
    {} as InstitutionName
  );
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const handleToggleConfirmModal = () => {
    setOpenConfirmModal((prevOpenConfirmModal) => !prevOpenConfirmModal);
  };
  const handleToggleSuccessModal = () => {
    setOpenSuccessModal((prevOpenSuccessModal) => !prevOpenSuccessModal);
  };

  const handleConfirmedSuccessModalClose = () => {
    handleToggleSuccessModal();
    history.push(dashboard());
  };

  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      if (requestBasicInfo.id) {
        const physicalExaminationTemp = await getPhysicalExamination(
          requestBasicInfo.id
        );
        if (physicalExaminationTemp !== null) {
          setPhysicalExamination(physicalExaminationTemp);
        }
      }
    })();
  }, [requestBasicInfo.id]);

  useEffect(() => {
    (async () => {
      if (requestBasicInfo.id && isEmpty(institutionName)) {
        const institutionName = await getInstitutionNameApi(
          requestBasicInfo.id
        );
        setInstitutionName(institutionName);
      }
    })();
  }, [institutionName, requestBasicInfo.id]);

  const handleFileChange = (files: File[]) => {
    if (files.length > 0) {
      if (documents.length + files.length > 10) {
        setShowMaxDoucmentsError(true);
      } else {
        let numberHelper = lastUploadDocumentId + 1;
        setShowMaxDoucmentsError(false);
        let tempDocuments: DocumentData[] = [];
        files.forEach((file) => {
          tempDocuments.push({
            id: numberHelper++,
            fileName: file.name,
            file: file,
          } as DocumentData);
        });
        setDocuments((prev) => [...prev, ...tempDocuments]);
        setLastUploadDocumentId(numberHelper);
      }
    }
  };

  const handleSentDocumentDownload = (document: DocumentData) => {
    if (document.fileID) {
      getPhysicalExaminationDocument(document.fileID);
    }
  };

  const handleRemoveDocument = (document: DocumentData) => {
    const tempDocuments = documents.filter((x) => x.id !== document.id);
    setDocuments(tempDocuments);
  };

  const handleDownloadDocument = (document: DocumentData) => {
    if (document.file) {
      saveAs(document.file, document.fileName);
    }
  };

  const getPhysicalExaminationRejectedDataItems = () => {
    return (
      <>
        {physicalExamination?.rejectedData.map((reject) => (
          <React.Fragment key={reject.requestCommentId}>
            <DocumentList
              onDocumentClick={handleSentDocumentDownload}
              documents={reject.documents}
              removable={false}
              color="primary"
            />
            <Typography variant="small" color="light">
              {`${hrDate(reject.date)} ${hrShortTime(reject.date)}, ${
                reject.user
              }, ${t("physicalExamination.reason")}: ${reject.requestComment}`}
            </Typography>
            <hr className={styles.line} />
          </React.Fragment>
        ))}
      </>
    );
  };

  const handleSendPhysicalExaminationDocumenation = async () => {
    if (requestBasicInfo.id && documents.length > 0) {
      let files: File[] = [];
      documents.forEach((x) => files.push(x.file!));
      await sendPhysicalExaminationDocumentation(
        toFormData<SendPhysicalExaminationDocumentation>({
          requestId: requestBasicInfo.id,
          files: files,
        })
      ).then(() => {
        handleToggleConfirmModal();
        handleToggleSuccessModal();
      });
    }
  };

  const getStatusColor = (statusId: number) => {
    switch (statusId) {
      case PhysicalExaminationStatusEnum.DocumentApproved:
        return "success";
      case PhysicalExaminationStatusEnum.ReadyForEvaluation:
        return "warning";
      case PhysicalExaminationStatusEnum.DocumentRejected:
        return "danger";
      default:
        return undefined;
    }
  };

  return (
    <>
      {physicalExamination?.statusId ===
      PhysicalExaminationStatusEnum.DocumentApproved ? (
        <>
          <div className={styles.documentApproved}>
            <IconWrapper color="noted-2">
              <DocumentApprovedIcon />
            </IconWrapper>

            <Typography
              className={styles.documentApprovedHeader}
              component="h1"
              variant="XXLargeBold"
            >
              {t("physicalExamination.documentApprovedHeader")}
            </Typography>
            <Typography variant="base">
              {t("physicalExamination.documentApprovedMessage")}
            </Typography>
          </div>
          <hr />
        </>
      ) : (
        physicalExamination && (
          <InfoPanel
            className={styles.mainWarning}
            color="warning"
            fullWidth
            message={t("physicalExamination.mainWarningMessage")}
          />
        )
      )}
      <div className={styles.wrapper}>
        {physicalExamination && physicalExamination?.statusId && (
          <>
            {physicalExamination.sentDocuments &&
              physicalExamination.sentDocuments.length > 0 && (
                <>
                  <Typography
                    className={styles.uploadedDocumentsHeader}
                    component="h1"
                    variant="largeBold"
                  >
                    {t("physicalExamination.uploadedDocuments")}
                  </Typography>
                  <DocumentList
                    onDocumentClick={handleSentDocumentDownload}
                    documents={physicalExamination.sentDocuments}
                    removable={false}
                    color="primary"
                  />
                </>
              )}
            <Typography
              className={styles.documentStatusLabel}
              variant="baseBold"
            >
              {t("physicalExamination.documentStatus")}
            </Typography>
            <div className={styles.documentStatus}>
              <Label color={getStatusColor(physicalExamination.statusId)}>
                {physicalExamination.status}
              </Label>
            </div>
          </>
        )}
        {physicalExamination &&
          physicalExamination?.statusId !==
            PhysicalExaminationStatusEnum.DocumentApproved &&
          physicalExamination?.statusId !==
            PhysicalExaminationStatusEnum.ReadyForEvaluation && (
            <>
              {" "}
              {isPhysicalExaminationAllowed(
                requestBasicInfo.applicantParentOib
              ) && (
                <>
                  {showMaxDoucmentsError ? (
                    <InfoPanel
                      message={t("documentUpload.maxDocumentErrorMessage")}
                      color="danger"
                      fullWidth
                    />
                  ) : (
                    <></>
                  )}
                  <Typography component="h3" variant="largeBold">
                    {t("physicalExamination.header")}
                  </Typography>
                  <UploadBox
                    className={styles.uploadArea}
                    maxSize={10}
                    maxFiles={11}
                    types=".pdf, .jpg, .jpeg, .png, .gif, .tiff, .bmp"
                    filesAction={(file: any): void => handleFileChange(file)}
                    info={t("documentUpload.uploadBoxInfo")}
                  />
                  <Typography variant="baseBold">
                    {t("documentUpload.uploadedDocuments")}
                  </Typography>
                </>
              )}
              {documents && documents.length > 0 ? (
                <DocumentList
                  fullWidth
                  documents={documents}
                  onDocumentClick={handleDownloadDocument}
                  onRemoveClick={handleRemoveDocument}
                />
              ) : (
                <Typography variant="base">
                  {t("physicalExamination.noDocuments")}
                </Typography>
              )}
              {isPhysicalExaminationAllowed(
                requestBasicInfo.applicantParentOib
              ) && (
                <div className={styles.actions}>
                  <Button
                    disabled={documents.length > 0 ? false : true}
                    size="sm"
                    onClick={handleToggleConfirmModal}
                  >
                    {t("physicalExamination.submitButton")}
                  </Button>
                </div>
              )}
            </>
          )}
        {physicalExamination?.statusId !==
          PhysicalExaminationStatusEnum.DocumentApproved &&
          physicalExamination?.rejectedData &&
          physicalExamination.rejectedData.length > 0 && (
            <>
              <Typography
                className={styles.uploadedDocumentsHeader}
                component="h1"
                variant="largeBold"
              >
                {t("physicalExamination.RejectedDocuments")}
              </Typography>
              <InfoPanel
                color="danger"
                message={t("physicalExamination.documentRejectedMessage")}
              />
              {getPhysicalExaminationRejectedDataItems()}
            </>
          )}
      </div>
      <Modal
        type="confirm"
        title={t("physicalExamination.confirmationModalTitle")}
        open={openConfirmModal}
        confirmText={t("common.yes")}
        cancelText={t("common.no")}
        onClose={handleToggleConfirmModal}
        onConfirm={handleSendPhysicalExaminationDocumenation}
      >
        <div>
          {t("physicalExamination.confirmationModalMessage", {
            institutionName: institutionName.name,
          })}
        </div>
      </Modal>
      <Modal
        type="message"
        open={openSuccessModal}
        confirmText={t("common.close")}
        onClose={handleConfirmedSuccessModalClose}
        onConfirm={handleConfirmedSuccessModalClose}
      >
        <div className={styles.modalMessageContent}>
          <IconWrapper color="noted-2">
            <DocumentSendSuccessIcon />
          </IconWrapper>
          <Typography component="h1" variant="largeBold">
            {t("physicalExamination.successModalTitle")}
          </Typography>
          {t("physicalExamination.successModalMessage")}
        </div>
      </Modal>
    </>
  );
}
